<script>
    import { mapGetters } from 'vuex';
    import globals from '@/util/globals';
    import ChevronIcon from '@/components/Icons/ChevronIcon.vue';
    import CloseOutlineIcon from '@/components/Icons/CloseOutlineIcon.vue';
    import CheckOutlineIcon from '@/components/Icons/CheckOutlineIcon.vue';
    import { INSPECTION_ITEM_RESPONSE, INSPECTION_ITEM_TYPE } from '@/constants';

    const filterValidItems = ({
        type, allConditionsMet, params, repeat,
    }) => !(params && params.automatic)
        && !(type === INSPECTION_ITEM_TYPE.CONDITIONAL && !allConditionsMet)
        && !repeat;

    export default {
        name: 'OutlineModalItem',
        components: {
            ChevronIcon,
            CloseOutlineIcon,
            CheckOutlineIcon,
        },
        props: {
            item: {
                type: Object,
                required: true,
            },
            page: {
                type: String,
                required: true,
            },
            responsesStatistics: {
                type: Object,
                required: true,
            },
            isOnlyOnePage: {
                type: Boolean,
                required: false,
                default: false,
            },
        },
        data: () => ({
            displayChildren: false,
        }),
        computed: {
            ...mapGetters({
                validationErrors: 'conduct/errors',
            }),
            responsesText() {
                const { responses, questions } = this.responsesStatistics[this.item.uuid];
                return `(${responses}/${questions})`;
            },
            responsesClass() {
                const { responses, questions } = this.responsesStatistics[this.item.uuid];
                return {
                    'text-red': responses !== questions,
                    'text-green': responses === questions,
                };
            },
            title() {
                if (this.item.type === INSPECTION_ITEM_TYPE.PAGE && this.item.params && this.item.params.header) return this.$t('Header');
                return this.item.title || '-';
            },
            items() {
                if (!this.item.items) return [];
                return this.item.items.filter(filterValidItems);
            },
            shouldDisplayChildren() {
                return this.displayChildren || this.isOnlyOnePage || this.item.type === INSPECTION_ITEM_TYPE.CONDITIONAL;
            },
            shouldDisplayCollapseButton() {
                return !this.isOnlyOnePage && this.item.type !== INSPECTION_ITEM_TYPE.CONDITIONAL;
            },
            inspectionItemType() {
                return INSPECTION_ITEM_TYPE;
            },
            inspectionItemResponse() {
                return INSPECTION_ITEM_RESPONSE;
            },
            isCollapsableItem() {
                const { PAGE, SECTION, CONDITIONAL } = INSPECTION_ITEM_TYPE;
                return [PAGE, SECTION, CONDITIONAL].includes(this.item.type);
            },
            isQuestionRespondedCorrectly() {
                return this.item.responded && !(this.item.uuid in this.validationErrors);
            },
            hasRequiredChildren() {
                return this.responsesStatistics[this.item.uuid].requiredChildren;
            },
        },
        methods: {
            itemClicked() {
                this.displayChildren = !this.displayChildren;
            },
            questionClicked() {
                this.$store.dispatch('conduct/outlineItemClicked', this.page);
                this.$nextTick(() => {
                    const elementToScroll = globals.getElementById(this.item.uuid);
                    if (elementToScroll && elementToScroll.scrollIntoView) elementToScroll.scrollIntoView();
                });
            },
        },
    };
</script>

<template>
    <button
        class="question flex items-center justify-between pl-2 py-3 border-b border-60 text-left hover:border-80"
        @click="questionClicked"
        v-if="item.type === inspectionItemType.QUESTION"
    >
        <div class="relative">
            <span v-if="item.required" class="absolute text-red -ml-2">*</span>
            <span>{{ title }}</span>
        </div>
        <div class="ml-1" v-if="item.response_type !== inspectionItemResponse.INSTRUCTION">
            <CheckOutlineIcon class="responded text-green" v-if="isQuestionRespondedCorrectly" />
            <CloseOutlineIcon class="text-red" v-else />
        </div>
    </button>
    <div v-else-if="isCollapsableItem" :class="item.type" class="flex flex-col">
        <button
            class="flex items-center py-3 border-b border-60 text-left font-semibold hover:border-80"
            @click="itemClicked"
            v-if="shouldDisplayCollapseButton"
        >
            <ChevronIcon :rotate="displayChildren ? 90 : 0" />
            <div class="flex-1 text-left relative ml-1">
                <span v-if="hasRequiredChildren" class="absolute text-red -ml-2">*</span>
                <span>{{ title }}</span>
            </div>
            <span class="font-normal" :class="responsesClass">{{ responsesText }}</span>
        </button>

        <div
            class="flex flex-col pl-4"
            v-if="shouldDisplayChildren"
            :class="{
                'pl-4': !shouldDisplayCollapseButton
            }"
        >
            <OutlineModalItem
                v-for="child in items" :key="child.uuid"
                :responses-statistics="responsesStatistics"
                :item="child"
                :page="page"
            />
        </div>
    </div>
    <div v-else></div>
</template>
